import { Stack } from "@mui/material";
//zui 

import { Card, CardContent } from 'src/@/components/ui/card';
import { RowInput } from 'src/components/forms/RowInput';
import { Textarea } from 'src/@/components/ui/textarea';
import { Input } from 'src/@/components/ui/input';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Button } from 'src/@/components/ui/button';
import { calcularTokens } from "src/utils/ai.utils";
import { useEffect, useState, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "src/@/components/ui/dialog";
import { Info, Hash, Wand2 } from "lucide-react";
import AdvancedPromptModal from "./components/AdvancedPromptModal";

function PromptGuideModal() {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" title="Como estruturar um prompt?">
                    <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Guia de Estruturação de Prompts</DialogTitle>
                    <DialogDescription>
                        Aprenda a criar prompts eficientes para seu agente de IA.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 text-sm text-slate-700">
                    <div className="bg-slate-50 p-4 rounded-lg border">
                        <h4 className="font-semibold text-slate-900 mb-2">Estrutura Ideal</h4>
                        <p className="mb-2">Uma estrutura organizada ajuda a IA a entender seu papel e limitações. Recomendamos o formato:</p>
                        <ol className="list-decimal pl-5 space-y-1 font-mono text-xs">
                            <li>IDENTIDADE E CONTEXTO</li>
                            <li>TOM E PERSONALIDADE</li>
                            <li>REGRAS DE SEGURANÇA (Anti-alucinação)</li>
                            <li>FLUXOS E CLASSIFICAÇÃO</li>
                            <li>EXEMPLOS (Few-Shot)</li>
                        </ol>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h4 className="font-semibold mb-1">1. Identidade e Contexto</h4>
                            <p className="text-xs text-muted-foreground">Defina quem é o agente, o que ele faz e o que NÃO faz. Estabeleça os limites claros.</p>
                            <pre className="mt-2 bg-slate-100 p-2 rounded text-xs font-mono whitespace-pre-wrap">
                                "Você é o Assistente Virtual da Loja X...{'\n'}
                                Seu objetivo é atender clientes do e-commerce..."
                            </pre>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-1">2. Regras e Restrições</h4>
                            <p className="text-xs text-muted-foreground">Instruções negativas são cruciais. Diga explicitamente o que é proibido.</p>
                            <pre className="mt-2 bg-slate-100 p-2 rounded text-xs font-mono whitespace-pre-wrap">
                                "NUNCA invente preços.{'\n'}
                                NÃO fale sobre política.{'\n'}
                                Se não souber, diga que vai consultar um humano."
                            </pre>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-1">3. Tom de Voz</h4>
                            <p className="text-xs text-muted-foreground">Defina a personalidade. É formal? Descontraído? Usa emojis?</p>
                            <pre className="mt-2 bg-slate-100 p-2 rounded text-xs font-mono whitespace-pre-wrap">
                                "Use emojis moderadamente.{'\n'}
                                Seja sempre cordial e paciente.{'\n'}
                                Evite gírias."
                            </pre>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-1">4. Exemplos (Few-Shot)</h4>
                            <p className="text-xs text-muted-foreground">Dê exemplos de perguntas e respostas ideais. Isso "treina" o modelo.</p>
                            <pre className="mt-2 bg-slate-100 p-2 rounded text-xs font-mono whitespace-pre-wrap">
                                User: "Quanto custa?"{'\n'}
                                Bot: "O valor depende do modelo..."{'\n'}
                            </pre>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function AiAgentPromptTab({
    prompt,
    changePrompt,
    aiAgent
}) {


    const [countTokens, setCountTokens] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [matchCount, setMatchCount] = useState(0);
    const textareaRef = useRef(null);
    const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);

    const promptValue = aiAgent?.config?.generalConfig?.type === "promptFree" ? aiAgent?.config?.promptFree?.prompt : aiAgent?.config?.promptGuide?.prompt;

    useEffect(() => {
        const tokens = calcularTokens(promptValue);
        setCountTokens(tokens);
    }, [promptValue]);

    // Identificar Seções do Prompt
    const sections = useMemo(() => {
        if (!promptValue) return [];
        const lines = promptValue.split('\n');
        const result = [];
        // Regex para capturar linhas que começam com números como "1.", "5.1", "10."
        const regex = /^(\d+(?:\.\d+)*)\.?\s+(.*)/;

        lines.forEach((line, index) => {
            const match = line.match(regex);
            if (match) {
                // Nível baseado na quantidade de pontos (ex: 1. = nível 0, 5.1 = nível 1)
                const level = match[1].split('.').length - 1;
                result.push({
                    number: match[1],
                    title: match[2].trim(),
                    lineIndex: index,
                    level: level > 0 ? level : 0
                });
            }
        });
        return result;
    }, [promptValue]);

    const scrollToLine = (lineIndex) => {
        if (textareaRef.current) {
            const lineHeight = 20; // Altura da linha (leading-5 = 20px)
            const lines = promptValue.split('\n');

            if (lineIndex >= lines.length) return;

            // Calcular scroll para centralizar visualmente a seção (aprox. 1/4 da altura)
            const textAreaHeight = textareaRef.current.clientHeight;
            const targetScroll = (lineIndex * lineHeight) - 80; // Deixa um respiro no topo
            textareaRef.current.scrollTop = targetScroll > 0 ? targetScroll : 0;

            // Calcular índice do caractere inicial
            let charIndex = 0;
            for (let i = 0; i < lineIndex; i++) {
                charIndex += lines[i].length + 1; // +1 para o \n
            }

            // Selecionar o texto da linha para destaque visual
            const lineLength = lines[lineIndex].length;
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(charIndex, charIndex + lineLength);
        }
    };

    // Função para buscar e destacar texto
    useEffect(() => {
        if (!searchTerm.trim() || !promptValue) {
            setMatchCount(0);
            setCurrentMatchIndex(0);
            return;
        }

        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const matches = promptValue.match(regex);
        setMatchCount(matches ? matches.length : 0);
        setCurrentMatchIndex(0);
    }, [searchTerm, promptValue]);

    // Função para navegar entre ocorrências
    const navigateToMatch = (direction) => {
        if (!searchTerm.trim() || matchCount === 0) return;

        const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const matches = [...promptValue.matchAll(regex)];

        if (matches.length === 0) return;

        let newIndex = currentMatchIndex;
        if (direction === 'next') {
            newIndex = (currentMatchIndex + 1) % matches.length;
        } else {
            newIndex = currentMatchIndex === 0 ? matches.length - 1 : currentMatchIndex - 1;
        }

        setCurrentMatchIndex(newIndex);

        // Scroll para a ocorrência
        if (textareaRef.current) {
            const match = matches[newIndex];
            const textBeforeMatch = promptValue.substring(0, match.index);
            const linesBefore = textBeforeMatch.split('\n').length - 1;
            const lineHeight = 20; // altura aproximada de uma linha
            textareaRef.current.scrollTop = linesBefore * lineHeight;
            textareaRef.current.setSelectionRange(match.index, match.index + match[0].length);
            textareaRef.current.focus();
        }
    };

    return (
        <>
            <Card className="my-7">
                <CardContent>
                    <Stack sx={{ my: 1 }} direction={"column"} spacing={1}>
                        <RowInput
                            label="Prompt"
                            input={
                                <div className="flex flex-col md:flex-row gap-4 h-[600px]">
                                    {/* Sidebar de Navegação e Ferramentas */}
                                    <div className="w-full md:w-1/3 flex flex-col border rounded-md bg-slate-50/50 p-2 overflow-hidden h-full gap-2">

                                        {/* Navegação */}
                                        <div className="flex-1 flex flex-col min-h-0">
                                            <div className="pb-2 border-b mb-2 px-1 flex justify-between items-center">
                                                <p className="text-sm font-semibold text-muted-foreground">Índice</p>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                                        title="Editor Avançado com IA"
                                                        onClick={() => setIsAdvancedModalOpen(true)}
                                                    >
                                                        <Wand2 className="h-4 w-4" />
                                                    </Button>
                                                    <PromptGuideModal />
                                                </div>
                                            </div>
                                            <ScrollArea className="flex-1">
                                                <div className="pr-3 mt-2">
                                                    {sections.length > 0 ? sections.map((section, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => scrollToLine(section.lineIndex)}
                                                            className={`
                                                            w-full text-left py-1.5 pr-2 rounded-md text-xs transition-all
                                                            hover:bg-slate-200/50 mb-0.5 flex items-center group relative
                                                            ${section.level === 0 ? 'font-semibold text-slate-800' : 'text-slate-600 font-normal'}
                                                        `}
                                                            style={{ paddingLeft: `${(section.level * 12) + 8}px` }}
                                                            title={`${section.number} ${section.title}`}
                                                        >
                                                            {/* Linha guia visual (opcional) */}
                                                            {section.level > 0 && (
                                                                <div className="absolute left-2 top-0 bottom-0 w-[1px] bg-slate-200/50 -ml-1.5" />
                                                            )}

                                                            <span className={`mr-2 shrink-0 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-sm font-mono text-[10px] ${section.level === 0 ? "bg-slate-100 text-slate-700 font-semibold" : "text-slate-500"}`}>
                                                                {section.number}
                                                            </span>

                                                            <span className="truncate flex-1 tracking-tight">{section.title}</span>
                                                        </button>
                                                    )) : (
                                                        <div className="flex flex-col items-center justify-center h-32 text-center p-4">
                                                            <p className="text-xs text-muted-foreground mb-2">Estrutura não detectada</p>
                                                            <p className="text-[10px] text-slate-400">Use números (1. Título) para criar o índice automaticamente.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </ScrollArea>
                                        </div>

                                        {/* Toolbox de Inserção Rápida */}
                                        <div className="border-t pt-2 flex flex-col min-h-0 shrink-0">
                                            <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">Adicionar Tópico</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { label: "Identidade", text: "IDENTIDADE E CONTEXTO\nVocê é um assistente virtual..." },
                                                    { label: "Personalidade", text: "TOM E PERSONALIDADE\n- Seja profissional e empático.\n- Use linguagem clara." },
                                                    { label: "Regras", text: "REGRAS E RESTRIÇÕES\n- Não invente informações (alucinação).\n- Responda apenas sobre o escopo definido." },
                                                    { label: "Classificação", text: "MATRIZ DE CLASSIFICAÇÃO\n1. Se o usuário perguntar sobre preços -> Direcionar para..." },
                                                    { label: "Passo a Passo", text: "FLUXO DE ATENDIMENTO\n1. Saudação\n2. Entender a demanda\n3. Resposta\n4. Encerramento" },
                                                    { label: "Exemplos", text: "EXEMPLOS (FEW-SHOT)\nUser: Olá\nAssistant: Olá! Como posso ajudar?" },
                                                ].map((item, i) => (
                                                    <Button
                                                        key={i}
                                                        variant="outline"
                                                        size="sm"
                                                        className="justify-start h-8 text-xs px-2"
                                                        onClick={() => {
                                                            const maxNum = sections.reduce((max, s) => {
                                                                const n = parseInt(s.number.split('.')[0]);
                                                                return !isNaN(n) && n > max ? n : max;
                                                            }, 0);
                                                            const nextNum = maxNum + 1;

                                                            const textArea = textareaRef.current;
                                                            if (textArea) {
                                                                const start = textArea.selectionStart;
                                                                const end = textArea.selectionEnd;
                                                                const text = promptValue;

                                                                // Se estiver no final ou vazio, adiciona nova seção numerada
                                                                // Se estiver no meio do texto, insere onde está o cursor
                                                                const isAtEnd = end === text.length;
                                                                const insertion = isAtEnd
                                                                    ? `\n\n${nextNum}. ${item.text}\n`
                                                                    : `\n${item.text}\n`;

                                                                const newContent = text.substring(0, end) + insertion + text.substring(end);
                                                                changePrompt(newContent);

                                                                setTimeout(() => {
                                                                    textArea.focus();
                                                                    textArea.selectionStart = textArea.selectionEnd = end + insertion.length;
                                                                }, 0);
                                                            } else {
                                                                changePrompt(promptValue + `\n\n${nextNum}. ${item.text}`);
                                                            }
                                                        }}
                                                    >
                                                        <span className="truncate">+ {item.label}</span>
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Editor Principal */}
                                    <div className="flex-1 flex flex-col h-full relative">
                                        <div className="mb-2 flex items-center gap-2">
                                            <Input
                                                type="text"
                                                placeholder="Pesquisar no prompt..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="flex-1 h-8 text-sm"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey) {
                                                        e.preventDefault();
                                                        navigateToMatch('next');
                                                    } else if (e.key === 'Enter' && e.shiftKey) {
                                                        e.preventDefault();
                                                        navigateToMatch('prev');
                                                    }
                                                }}
                                            />
                                            {searchTerm && matchCount > 0 && (
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                                                    <span>{currentMatchIndex + 1} de {matchCount}</span>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => navigateToMatch('prev')}
                                                        title="Anterior (Shift+Enter)"
                                                    >
                                                        ↑
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => navigateToMatch('next')}
                                                        title="Próximo (Ctrl+Enter)"
                                                    >
                                                        ↓
                                                    </Button>
                                                </div>
                                            )}
                                            {searchTerm && matchCount === 0 && (
                                                <span className="text-xs text-muted-foreground">0 encontrados</span>
                                            )}
                                        </div>
                                        <Textarea
                                            ref={textareaRef}
                                            value={promptValue}
                                            className="flex-1 min-h-0 resize-none font-mono text-sm leading-5"
                                            onChange={(e) => changePrompt(e.target.value)}
                                            spellCheck={false}
                                        />
                                        <div className="mt-1 flex justify-between items-center text-xs text-muted-foreground">
                                            <span className="italic">Use seções numeradas (1. Título) para criar o índice lateral</span>
                                            <span>Tokens aprox: {countTokens}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </Stack>
                </CardContent>
            </Card>
            <AdvancedPromptModal
                open={isAdvancedModalOpen}
                onOpenChange={setIsAdvancedModalOpen}
                currentPrompt={promptValue}
                onSave={(newVal) => {
                    changePrompt(newVal);
                    // Opcional: Fechar o modal após salvar se desejar, mas o usuário pode querer continuar editando.
                    // setIsAdvancedModalOpen(false); 
                }}
                aiAgent={aiAgent}
            />
        </>

    )
}